import { useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, MagnifyingGlass, Export, Star, Briefcase, ArrowLeft, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, icon: Icon, color = "zinc" }) {
  return (
    <div className="border border-zinc-200 dark:border-zinc-800 p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} weight="bold" className="text-zinc-400 dark:text-zinc-500" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-400 dark:text-zinc-500">{label}</span>
      </div>
      <p className={`text-2xl font-light font-mono text-${color}-600 dark:text-${color}-400`}>{value}</p>
    </div>
  );
}

export default function AdminDashboard({ onBack }) {
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { password });
      setAuthenticated(true);
    } catch {
      setError("Invalid password");
    }
  };

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    axios
      .get(`${API}/admin/analytics`, { headers: { "x-admin-password": password } })
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [authenticated, password]);

  if (!authenticated) {
    return (
      <div className="w-full min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center" data-testid="admin-login-page">
        <div className="w-full max-w-xs space-y-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors mb-4"
            data-testid="admin-back-btn"
          >
            <ArrowLeft size={14} weight="bold" />
            Back to Screener
          </button>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} weight="bold" className="text-zinc-400 dark:text-zinc-500" />
            <h1 className="text-lg font-medium text-zinc-950 dark:text-zinc-100">Admin Dashboard</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            placeholder="Enter admin password"
            className="w-full border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-2.5 text-sm font-mono rounded-none shadow-none focus:outline-none focus:ring-1 focus:ring-zinc-950 dark:focus:ring-zinc-400 placeholder:text-zinc-300 dark:placeholder:text-zinc-600 text-zinc-950 dark:text-zinc-100"
            data-testid="admin-password-input"
          />
          <button
            onClick={handleLogin}
            className="w-full py-2.5 text-[11px] font-mono uppercase tracking-wider bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-300 transition-colors"
            data-testid="admin-login-btn"
          >
            Access Dashboard
          </button>
          {error && <p className="text-xs font-mono text-red-500" data-testid="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-white dark:bg-zinc-950 flex items-center justify-center">
        <div className="inline-block w-6 h-6 border-2 border-zinc-200 dark:border-zinc-700 border-t-zinc-950 dark:border-t-zinc-100 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="w-full min-h-screen bg-white dark:bg-zinc-950 transition-colors" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft size={14} weight="bold" />
              Back
            </button>
            <div className="w-px h-5 bg-zinc-200 dark:bg-zinc-800" />
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="bold" className="text-zinc-400 dark:text-zinc-500" />
              <h1 className="text-lg font-medium text-zinc-950 dark:text-zinc-100">Admin Analytics</h1>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
          <StatCard label="Total Stocks" value={data.total_stocks_screened} icon={ShieldCheck} />
          <StatCard label="Total Searches" value={data.total_searches} icon={MagnifyingGlass} />
          <StatCard label="Unique Searches" value={data.unique_searches} icon={MagnifyingGlass} />
          <StatCard label="CSV Exports" value={data.total_exports} icon={Export} />
          <StatCard label="Watchlist Adds" value={data.total_watchlist_adds} icon={Star} />
          <StatCard label="Portfolio Screens" value={data.total_portfolio_screens} icon={Briefcase} />
        </div>

        {/* Top Tickers & Sectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          <div className="border border-zinc-200 dark:border-zinc-800 p-5" data-testid="top-tickers">
            <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-4">
              Most Searched Tickers
            </h3>
            {data.top_searched_tickers.length === 0 ? (
              <p className="text-xs font-mono text-zinc-300 dark:text-zinc-600">No data yet</p>
            ) : (
              <div className="space-y-0">
                {data.top_searched_tickers.map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-50 dark:border-zinc-800/50">
                    <span className="text-sm font-mono font-medium text-zinc-950 dark:text-zinc-100">{t.ticker}</span>
                    <span className="text-xs font-mono text-zinc-400 dark:text-zinc-500">{t.count} searches</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-zinc-200 dark:border-zinc-800 p-5" data-testid="top-sectors">
            <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-4">
              Most Searched Sectors
            </h3>
            {data.top_searched_sectors.length === 0 ? (
              <p className="text-xs font-mono text-zinc-300 dark:text-zinc-600">No data yet</p>
            ) : (
              <div className="space-y-0">
                {data.top_searched_sectors.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-50 dark:border-zinc-800/50">
                    <span className="text-sm text-zinc-700 dark:text-zinc-300">{s.sector}</span>
                    <span className="text-xs font-mono text-zinc-400 dark:text-zinc-500">{s.count} filters</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Events */}
        <div className="border border-zinc-200 dark:border-zinc-800 p-5" data-testid="recent-events">
          <h3 className="text-[10px] tracking-[0.25em] uppercase font-mono text-zinc-400 dark:text-zinc-500 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-0 max-h-[300px] overflow-y-auto">
            {data.recent_events.map((ev, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-50 dark:border-zinc-800/50 text-xs font-mono">
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                    {ev.event}
                  </span>
                  <span className="text-zinc-500 dark:text-zinc-400">
                    {JSON.stringify(ev.data).slice(0, 50)}
                  </span>
                </div>
                <span className="text-zinc-300 dark:text-zinc-600 flex items-center gap-1 shrink-0">
                  <Clock size={10} />
                  {new Date(ev.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
